import * as React from 'react';
import { useRecoilValue } from 'recoil';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { diffOptionsState } from './options';

type DiffMode = 'all-pages' | 'only-changes';
interface DiffContextType {
    /** The pages to render, annotated with their diff. */
    pages: hiveCore.RevisionTreePage[];

    /**
     * Base revision used for the diff. If undefined, no diff is shown.
     */
    baseRevision: hiveCore.Revision | undefined;

    /**
     * Mode for the diff. If null, no diff is shown.
     */
    diffMode: DiffMode | null;

    /**
     * The context also provides the revision used to calculate pages, in order to maintain consistency between pages and revision
     */
    revision: hiveCore.Revision;
}

export const DiffContext = React.createContext<DiffContextType>({
    baseRevision: undefined,
    pages: [],
    diffMode: null,
    revision: hiveCore.createEmptyRevision({ contentId: 'placeholder-space' }),
});

/**
 * Provide high-level diff context for the entire space. It does the following:
 *
 * - Determine whether we need to show a diff (and the complexity of the diff) based on the current screen
 * - Determine which revision to use as the base for the diff
 * - Compute the diff for the pages
 *
 * It does not compute diffs for content, which is done at a lower level.
 */
export function DiffContextProvider(props: {
    children: React.ReactNode;
    revision: hiveCore.Revision;
    sourceRevision: hiveCore.Revision;
    contentId: string;
    changeRequest: api.ChangeRequest | undefined;
    screen: NavigationScreenSpaceState | undefined;
}): React.ReactElement {
    const { revision, contentId, screen, changeRequest, children } = props;

    const hiveClient = useHiveClient();
    const diffOptions = useRecoilValue(diffOptionsState);

    const baseRevisionId = getBaseRevisionId(revision, changeRequest, screen);
    let baseRevision = baseRevisionId
        ? getSuspense(hiveClient.store.getRevision(contentId, baseRevisionId, { metadata: false }))
        : undefined;
    baseRevision = baseRevision
        ? getSuspense(hiveClient.getContentComputer(contentId).getCompleteRevision(baseRevision))
              .revision
        : undefined;

    const diffMode = ((): DiffMode | null => {
        if (!baseRevision || !screen) {
            // without a base revision, no diff
            return null;
        }

        if (screen.revision && screen.mode !== 'diff') {
            // when viewing the history but with diff disabled
            return null;
        }

        if (screen.mode === 'diff') {
            // if we're in explicit diff mode, full diff
            return diffOptions.onlyShowChangedPages ? 'only-changes' : 'all-pages';
        }

        return null;
    })();

    const value = React.useMemo<DiffContextType>(() => {
        // If there's no baseRevision or we are not supposed to show a diff
        if (diffMode === null || !baseRevision) {
            return {
                pages: hiveCore.createTreePages(revision.pages, revision.rootPages),
                baseRevision,
                diffMode,
                revision,
            };
        }

        const { pages } = hiveCore.diffRevisionPages(revision, baseRevision, {
            includeDeleted: true,
            includeUnchanged: diffMode === 'all-pages',

            // Ignore pages with changes in metadata / variables. We don't have diff UI to display those.
            compareMetadata: false,
        });

        return {
            pages,
            baseRevision,
            diffMode,
            revision,
            diffOptions,
        };
    }, [revision, baseRevision, diffMode]);

    return <DiffContext.Provider value={value}>{children}</DiffContext.Provider>;
}

/**
 * Determine the base revision for the diff, and whether the diff should be deep.
 */
function getBaseRevisionId(
    revision: hiveCore.Revision | null | undefined,
    changeRequest: api.ChangeRequest | null | undefined,
    screen: NavigationScreenSpaceState | undefined
): string | undefined {
    if (changeRequest) {
        return changeRequest.revisionMergedAncestor || changeRequest.revisionInitial;
    }

    if (screen?.revision && revision && revision.parents[0]) {
        return revision.parents[0];
    }

    return undefined;
}
